import { OM } from './offerConstants';
import type {
  Deferred,
  FetchOptions,
  ToastItem,
  CreateOfferPayload,
  UpdateOfferPayload,
  ToggleOfferStatusPayload,
  BulkArchiveOffersPayload,
  BulkDeleteOffersPayload,
  CreatePromoCodePayload,
  UpdatePromoCodePayload,
  OffersSummary,
} from './offerTypes';
import type { Offer, PromoCode } from '../../components/offers/data/types';

export type OfferManagementAction = ReturnType<
  | typeof offersSetEditing
  | typeof offersClearCache
  | typeof fetchOfferByIdRequest
  | typeof fetchOfferByIdSuccess
  | typeof fetchOfferByIdFailure
  | typeof fetchOffersRequest
  | typeof fetchOffersSuccess
  | typeof fetchOffersFailure
  | typeof createOfferRequest
  | typeof createOfferDone
  | typeof createOfferFailure
  | typeof updateOfferRequest
  | typeof updateOfferDone
  | typeof updateOfferFailure
  | typeof deleteOfferRequest
  | typeof deleteOfferDone
  | typeof deleteOfferFailure
  | typeof bulkDeleteOffersRequest
  | typeof bulkDeleteOffersDone
  | typeof bulkDeleteOffersFailure
  | typeof toggleOfferStatusRequest
  | typeof toggleOfferStatusDone
  | typeof toggleOfferStatusFailure
  | typeof bulkArchiveOffersRequest
  | typeof bulkArchiveOffersDone
  | typeof bulkArchiveOffersFailure
  | typeof offersAdd
  | typeof offersUpdate
  | typeof offersRemove
  | typeof promoCodesSetEditing
  | typeof fetchPromoCodesRequest
  | typeof fetchPromoCodesSuccess
  | typeof fetchPromoCodesFailure
  | typeof createPromoCodeRequest
  | typeof createPromoCodeDone
  | typeof createPromoCodeFailure
  | typeof updatePromoCodeRequest
  | typeof updatePromoCodeDone
  | typeof updatePromoCodeFailure
  | typeof deletePromoCodeRequest
  | typeof deletePromoCodeDone
  | typeof deletePromoCodeFailure
  | typeof promoCodesAdd
  | typeof promoCodesUpdate
  | typeof promoCodesRemove
  | typeof toastAdd
  | typeof toastRemove
>;

// ── Offers ──

export const offersSetEditing = (id: string | null) =>
  ({ type: OM.OFFERS_SET_EDITING, payload: id } as const);

export const offersClearCache = () =>
  ({ type: OM.OFFERS_CLEAR_CACHE } as const);

export const fetchOfferByIdRequest = (payload: {
  offerId: string;
  locationId: string;
  token?: string | null;
  deferred?: Deferred<Offer>;
}) => ({ type: OM.FETCH_OFFER_BY_ID_REQUEST, payload } as const);

export const fetchOfferByIdSuccess = (payload: { offer: Offer }) =>
  ({ type: OM.FETCH_OFFER_BY_ID_SUCCESS, payload } as const);

export const fetchOfferByIdFailure = (payload: { error: string }) =>
  ({ type: OM.FETCH_OFFER_BY_ID_FAILURE, payload } as const);

export const fetchOffersRequest = (payload: {
  locationId: string;
  token?: string | null;
  options?: FetchOptions;
  deferred?: Deferred;
}) => ({ type: OM.FETCH_OFFERS_REQUEST, payload } as const);

export const fetchOffersSuccess = (payload: { offers: Offer[]; locationId: string; summary?: OffersSummary }) =>
  ({ type: OM.FETCH_OFFERS_SUCCESS, payload } as const);

export const fetchOffersFailure = (payload: { error: string }) =>
  ({ type: OM.FETCH_OFFERS_FAILURE, payload } as const);

export const createOfferRequest = (payload: {
  data: CreateOfferPayload;
  token?: string | null;
  deferred?: Deferred<Offer>;
}) => ({ type: OM.CREATE_OFFER_REQUEST, payload } as const);

export const createOfferDone = (payload: { offer?: Offer; error?: string | null }) =>
  ({ type: OM.CREATE_OFFER_SUCCESS, payload } as const);

export const createOfferFailure = (payload: { error: string }) =>
  ({ type: OM.CREATE_OFFER_FAILURE, payload } as const);

export const updateOfferRequest = (payload: {
  data: UpdateOfferPayload;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.UPDATE_OFFER_REQUEST, payload } as const);

export const updateOfferDone = (payload: { error?: string | null }) =>
  ({ type: OM.UPDATE_OFFER_SUCCESS, payload } as const);

export const updateOfferFailure = (payload: { error: string }) =>
  ({ type: OM.UPDATE_OFFER_FAILURE, payload } as const);

export const deleteOfferRequest = (payload: {
  offerId: string;
  locationId: string;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.DELETE_OFFER_REQUEST, payload } as const);

export const deleteOfferDone = (payload: { offerId?: string; error?: string | null }) =>
  ({ type: OM.DELETE_OFFER_SUCCESS, payload } as const);

export const deleteOfferFailure = (payload: { error: string }) =>
  ({ type: OM.DELETE_OFFER_FAILURE, payload } as const);

export const bulkDeleteOffersRequest = (payload: {
  data: BulkDeleteOffersPayload;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.BULK_DELETE_OFFERS_REQUEST, payload } as const);

export const bulkDeleteOffersDone = (payload: { offerIds?: string[]; error?: string | null }) =>
  ({ type: OM.BULK_DELETE_OFFERS_SUCCESS, payload } as const);

export const bulkDeleteOffersFailure = (payload: { error: string }) =>
  ({ type: OM.BULK_DELETE_OFFERS_FAILURE, payload } as const);

export const toggleOfferStatusRequest = (payload: {
  data: ToggleOfferStatusPayload;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.TOGGLE_OFFER_STATUS_REQUEST, payload } as const);

export const toggleOfferStatusDone = (payload: { error?: string | null }) =>
  ({ type: OM.TOGGLE_OFFER_STATUS_SUCCESS, payload } as const);

export const toggleOfferStatusFailure = (payload: { error: string }) =>
  ({ type: OM.TOGGLE_OFFER_STATUS_FAILURE, payload } as const);

export const bulkArchiveOffersRequest = (payload: {
  data: BulkArchiveOffersPayload;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.BULK_ARCHIVE_OFFERS_REQUEST, payload } as const);

export const bulkArchiveOffersDone = (payload: { offerIds?: string[]; error?: string | null }) =>
  ({ type: OM.BULK_ARCHIVE_OFFERS_SUCCESS, payload } as const);

export const bulkArchiveOffersFailure = (payload: { error: string }) =>
  ({ type: OM.BULK_ARCHIVE_OFFERS_FAILURE, payload } as const);

export const offersAdd = (offer: Offer) =>
  ({ type: OM.OFFERS_ADD, payload: offer } as const);

export const offersUpdate = (payload: { id: string; updates: Partial<Offer> }) =>
  ({ type: OM.OFFERS_UPDATE, payload } as const);

export const offersRemove = (id: string) =>
  ({ type: OM.OFFERS_REMOVE, payload: id } as const);

// ── Promo Codes ──

export const promoCodesSetEditing = (id: string | null) =>
  ({ type: OM.PROMO_CODES_SET_EDITING, payload: id } as const);

export const fetchPromoCodesRequest = (payload: {
  offerId: string;
  locationId: string;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.FETCH_PROMO_CODES_REQUEST, payload } as const);

export const fetchPromoCodesSuccess = (payload: { promoCodes: PromoCode[] }) =>
  ({ type: OM.FETCH_PROMO_CODES_SUCCESS, payload } as const);

export const fetchPromoCodesFailure = (payload: { error: string }) =>
  ({ type: OM.FETCH_PROMO_CODES_FAILURE, payload } as const);

export const createPromoCodeRequest = (payload: {
  data: CreatePromoCodePayload;
  token?: string | null;
  deferred?: Deferred<PromoCode>;
}) => ({ type: OM.CREATE_PROMO_CODE_REQUEST, payload } as const);

export const createPromoCodeDone = (payload: { promoCode?: PromoCode; error?: string | null }) =>
  ({ type: OM.CREATE_PROMO_CODE_SUCCESS, payload } as const);

export const createPromoCodeFailure = (payload: { error: string }) =>
  ({ type: OM.CREATE_PROMO_CODE_FAILURE, payload } as const);

export const updatePromoCodeRequest = (payload: {
  data: UpdatePromoCodePayload;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.UPDATE_PROMO_CODE_REQUEST, payload } as const);

export const updatePromoCodeDone = (payload: { error?: string | null }) =>
  ({ type: OM.UPDATE_PROMO_CODE_SUCCESS, payload } as const);

export const updatePromoCodeFailure = (payload: { error: string }) =>
  ({ type: OM.UPDATE_PROMO_CODE_FAILURE, payload } as const);

export const deletePromoCodeRequest = (payload: {
  promoCodeId: string;
  offerId: string;
  locationId: string;
  token?: string | null;
  deferred?: Deferred;
}) => ({ type: OM.DELETE_PROMO_CODE_REQUEST, payload } as const);

export const deletePromoCodeDone = (payload: { promoCodeId?: string; error?: string | null }) =>
  ({ type: OM.DELETE_PROMO_CODE_SUCCESS, payload } as const);

export const deletePromoCodeFailure = (payload: { error: string }) =>
  ({ type: OM.DELETE_PROMO_CODE_FAILURE, payload } as const);

export const promoCodesAdd = (promoCode: PromoCode) =>
  ({ type: OM.PROMO_CODES_ADD, payload: promoCode } as const);

export const promoCodesUpdate = (payload: { id: string; updates: Partial<PromoCode> }) =>
  ({ type: OM.PROMO_CODES_UPDATE, payload } as const);

export const promoCodesRemove = (id: string) =>
  ({ type: OM.PROMO_CODES_REMOVE, payload: id } as const);

// ── Toast ──

export const toastAdd = (payload: Omit<ToastItem, 'id'> & { id?: string }) =>
  ({ type: OM.TOAST_ADD, payload } as const);

export const toastRemove = (id: string) =>
  ({ type: OM.TOAST_REMOVE, payload: id } as const);
